import { Event, Hub, Severity } from "@sentry/types";
import { IExceptionHandlerService } from "angular";
import { $InjectorLike } from "angular-ui-router";
import { HttpResponseError } from "../interceptors/apex-api-response.interceptor";

const logError = console.error.bind(console);

// Sentry decorator adapted from https://github.com/getsentry/sentry-javascript/blob/master/packages/integrations/src/angular.ts
// See https://github.com/angular/angular.js/blob/v1.4.7/src/minErr.js
const angularPattern = /^\[((?:[$a-zA-Z0-9]+:)?(?:[$a-zA-Z0-9]+))\] (.*?)\n?(\S+)$/;
export function sentryExceptionHandlerDecorator($delegate: IExceptionHandlerService, $injector: $InjectorLike) {
    'ngInject';
    return (exception: any, cause: string) => {
        const hub = $injector.get<Hub>("sentryExceptionHandlerHub");
        if (hub) {
            // Don't mark errors that make it to the exception handler as "handled".
            // This is to distinguish from errors that are logged to $log.error.
            notifySentryHubError(hub, exception, cause, false);
            // Stop processing additional handlers, since that will eventually
            // send output to the $log service and submit a duplicate error to Sentry.
            logError(exception);
            return;
        }
        // Otherwise, let Sentry logic fall through to the logger.
        $delegate(exception, cause);
    };
}

export function notifySentryHubError(hub: Hub, error: Error, cause: string,
    handled = true, ...extra: any[]) {

    hub.withScope(scope => {
        if (cause) {
            scope.setExtra('cause', cause);
        }
        if (extra && extra.length) {
            scope.setExtra('messages', extra);
        }

        scope.addEventProcessor((event: Event) => {
            const ex = event.exception && event.exception.values && event.exception.values[0];

            event.logger = "angularjs";
            if (ex) {
                ex.mechanism.handled = handled;
                const matches = angularPattern.exec(ex.value || '');

                if (matches) {
                    // This type now becomes something like: $rootScope:inprog
                    ex.type = matches[1];
                    ex.value = matches[2];
                    event.message = `${ex.type}: ${ex.value}`;
                    // auto set a new tag specifically for the angular error url
                    event.extra = {
                        ...event.extra,
                        angularDocs: matches[3].substr(0, 250),
                    };
                }
                if (error instanceof HttpResponseError && error.status === 400) {
                    // Provide config and response data as extra data for 400 Bad Request events.
                    const data = error.data;
                    // Bad Request is typically user error, so downgrade the severity to Warning.
                    event.level = Severity.Warning;
                    event.extra = {
                        ...event.extra,
                        status: error.status,
                        statusText: error.statusText,
                        // Attempt to serialize response data.
                        data: JSON.stringify(data)
                    }
                }
            }

            return event;
        });

        hub.captureException(error);
    });
}
